import { delay } from "redux-saga";
import { put, takeEvery, takeLatest, call, select } from "redux-saga/effects";
import {
    ACTIONS,
    setStudentResponse,
    deleteResponseSuccess,
    deleteResponseFail,
    setAdminPermissionsFail,
    setAdminPermissionsSuccess,
    deleteAdminSuccess,
    setResponseAdminSuccess,
    setResponseAdminFail,
    setLoading,
    needsReviewListFail,
    needsReviewListSuccess,
    schemaListSuccess,
    schemaListFail,
    clientListFail,
    clientListSuccess,
    schemaUpdateFail,
    schemaUpdateSuccess,
    schemaCreateSuccess,
    schemaCreateFail,
    deleteClientFail,
    deleteClientSuccess,
    requestClientList,
    selectStudent
} from "./reducer";
import { fetchDataPromise, UnauthorizedError } from "../../utils/index";
import { logout, setAlertWarning } from "../WriteClass/reducer";
import { get, pickBy, omit, cloneDeep, isString, isNumber } from "lodash";

export function* setAdminPermissions(action) {
    const user = pickBy(get(action, ["payload"], {}), val => val !== null);
    const user_id = get(user, ["id"]);

    if (user_id !== undefined) {
        try {
            const response = yield fetchDataPromise(
                "/admin/update/" + user_id,
                {
                    method: "PUT",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(user)
                }
            );
            const data = yield response.json();
            yield put(setAdminPermissionsSuccess(data));
        } catch (e) {
            yield put(setAdminPermissionsFail());
            if (e instanceof UnauthorizedError) {
                console.log(
                    "got an unauthorized error, logging out, redirecting to login page"
                );
                yield put(logout());
            }
        }
    }
}

export function* setResponseAdmin(action) {
    yield call(delay, 1000);
    const id = get(action, ["payload", "id"]);
    // deep copy to prevent modifying the local copy w/ user info
    let body = cloneDeep(get(action, "payload"));
    // body may have 'schema_version' and/or 'user', which needs to be removed
    delete body["schema_version"];
    delete body["user"];

    if (body === undefined) {
        console.warn(
            "saga::setResponseAdmin -- no body found in action:",
            action
        );
        return;
    }

    try {
        const response = yield fetchDataPromise("/response/admin/" + id, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });

        const data = yield response.json();
        yield put(setResponseAdminSuccess(data));
        const selected_student_id = yield select(state => {
            return get(state, ["admin", "selected_student_id"])
        }
        );
        if (selected_student_id !== undefined) {
            yield put(selectStudent(selected_student_id));
        }
    } catch (e) {
        console.error(e);
        yield put(setResponseAdminFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* getNeedsReviewList(action) {
    yield call(delay, 500);
    // get responses
    try {
        const response = yield fetchDataPromise(
            "/response?done=true&needs_review=true&approved=null&inc_user=true",
            {
                method: "GET"
            }
        );
        const data = yield response.json();
        yield put(needsReviewListSuccess(data));
    } catch (e) {
        console.error(e);
        yield put(needsReviewListFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* onSelectStudent(action) {
    let userid = get(action, "payload");
    if (userid === undefined) {
        return;
    }
    yield put(setLoading(true));
    yield call(delay, 500);
    // get user info
    try {
        const response = yield fetchDataPromise(
            "/response/view/user/" + userid,
            {
                method: "GET"
            }
        );
        const data = yield response.json();
        yield put(setStudentResponse(data));
    } catch (e) {
        yield put(setStudentResponse(undefined));
        console.log(e);
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* deleteStudent(action) {
    const responseid = get(action, ["payload", "id"]);
    try {
        yield fetchDataPromise("/response/" + responseid, {
            method: "DELETE"
        });

        yield put(deleteResponseSuccess());
    } catch (e) {
        yield put(deleteResponseFail());
        console.log(e);
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* deleteAdmin(action) {
    const user = get(action, ["payload"]);
    const user_id = get(action, ["payload", "id"]);

    try {
        yield fetchDataPromise("/admin/delete/" + user_id, {
            method: "DELETE"
        });

        yield put(deleteAdminSuccess(user));
    } catch (e) {
        console.log("saga::deleteAdmin failed:", e);
        yield put(deleteResponseFail());
        yield put(
            setAlertWarning({
                message:
                    "Failed to delete admin. Admin may have related integrations",
                timeout: 3000
            })
        );
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* getSchemaList(action) {
    yield call(delay, 500);
    // get all schemas
    try {
        const response = yield fetchDataPromise("/schema", {
            method: "GET"
        });
        const data = yield response.json();
        yield put(schemaListSuccess(data));
    } catch (e) {
        console.error(e);
        yield put(schemaListFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* getClientList(action) {
    yield call(delay, 500);
    // get all schemas
    try {
        const response = yield fetchDataPromise("/clients", {
            method: "GET"
        });
        const data = yield response.json();
        yield put(clientListSuccess(data));
    } catch (e) {
        console.error(e);
        yield put(clientListFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* deleteClient(action) {
    if (!isString(action.payload) && !isNumber(action.payload)) {
        return;
    }
    try {
        yield fetchDataPromise("/clients/" + action.payload, {
            method: "DELETE"
        });

        yield put(deleteClientSuccess());
        yield put(requestClientList());
    } catch (e) {
        console.error(e);
        yield put(deleteClientFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* schemaUpdate(action) {
    // update schema
    try {
        const id = get(action, ["payload", "id"]);
        const body = omit(get(action, "payload"), "id");
        const response = yield fetchDataPromise(`/schema/${id}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });
        yield response.json();
        yield put(schemaUpdateSuccess());
    } catch (e) {
        console.error(e);
        yield put(schemaUpdateFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

export function* schemaCreate(action) {
    // update schema
    try {
        const body = get(action, "payload") || {};
        const response = yield fetchDataPromise("/schema", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });
        const data = yield response.json();
        yield put(schemaCreateSuccess(data.id));
    } catch (e) {
        console.error(e);
        yield put(schemaCreateFail());
        if (e instanceof UnauthorizedError) {
            console.log(
                "got an unauthorized error, logging out, redirecting to login page"
            );
            yield put(logout());
        }
    }
}

// Our watcher Saga: spawn a new incrementAsync task on each INCREMENT_ASYNC
function* watchSelectStudent() {
    yield takeLatest(ACTIONS.SELECT_STUDENT, onSelectStudent);
}

function* watchRequestDelete() {
    yield takeEvery(ACTIONS.REQUEST_DELETE_RESPONSE, deleteStudent);
}

function* watchRequestSetAdminPermissions() {
    yield takeEvery(ACTIONS.REQUEST_SET_ADMIN_PERMISSIONS, setAdminPermissions);
}

function* watchRequestDeleteAdmin() {
    yield takeEvery(ACTIONS.REQUEST_DELETE_ADMIN, deleteAdmin);
}

function* watchRequestSetResponseAdmin() {
    yield takeLatest(ACTIONS.REQUEST_SET_RESPONSE_ADMIN, setResponseAdmin);
}

function* watchRequestGetResponses() {
    yield takeLatest(ACTIONS.REQUEST_NEEDS_REVIEW_LIST, getNeedsReviewList);
}

function* watchRequestGetSchemas() {
    yield takeLatest(ACTIONS.REQUEST_SCHEMA_LIST, getSchemaList);
}

function* watchRequestGetClients() {
    yield takeLatest(ACTIONS.REQUEST_CLIENT_LIST, getClientList);
}

function* watchRequestUpdateSchema() {
    yield takeEvery(ACTIONS.REQUEST_UPDATE_SCHEMA, schemaUpdate);
}

function* watchRequestCreateSchema() {
    yield takeEvery(ACTIONS.REQUEST_CREATE_SCHEMA, schemaCreate);
}

function* watchRequestDeleteClient() {
    yield takeEvery(ACTIONS.REQUEST_DELETE_CLIENT, deleteClient);
}

export const ADMIN_SAGAS = [
    watchSelectStudent(),
    watchRequestDelete(),
    watchRequestSetAdminPermissions(),
    watchRequestDeleteAdmin(),
    watchRequestSetResponseAdmin(),
    watchRequestGetResponses(),
    watchRequestGetSchemas(),
    watchRequestGetClients(),
    watchRequestUpdateSchema(),
    watchRequestCreateSchema(),
    watchRequestDeleteClient()
];



// WEBPACK FOOTER //
// ./src/store/Admin/saga.js